/**
 * Reseller customer account pages — Phase 8.5.
 *
 * Four sub-pages sharing an AccountShell + sidebar nav:
 *   - Profile     (name, email, phone, address)
 *   - Security    (change password, MFA setup/disable, backup codes)
 *   - Preferences (per-category email opt-outs)
 *   - Account     (export data, delete account)
 *
 * Mounted at /r/:slug/customer/account/* (slug mode) AND bare
 * /customer/account/* (host mode). useScopedPath emits the right shape
 * either way.
 */
import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import axios from 'axios'
import { Shell } from './ResellerCustomerPortal'

// VITE_API_ORIGIN: required on prod where the SPA host doesn't proxy /api/*.
const _apiOrigin = import.meta.env.VITE_API_ORIGIN || window.location.origin
const API = axios.create({ baseURL: `${_apiOrigin}/api/public/customer-portal`, validateStatus: () => true })
function authHeaders() {
  const t = localStorage.getItem('rcp_session')
  return t ? { Authorization: 'Bearer ' + t } : {}
}

function useScopedPath() {
  const { resellerSlug } = useParams()
  return (rel) => {
    const trimmed = String(rel || '').replace(/^\/+/, '')
    if (resellerSlug && resellerSlug !== '_') return `/r/${resellerSlug}/customer/${trimmed}`
    if (trimmed === 'login') return '/customer/sign-in'
    return `/customer/${trimmed}`
  }
}

// ─── Page wrappers (each route renders one of these) ──────────────────

export function AccountProfile()     { return <AccountShell page="profile"><Profile /></AccountShell> }
export function AccountSecurity()    { return <AccountShell page="security"><Security /></AccountShell> }
export function AccountPreferences() { return <AccountShell page="preferences"><Preferences /></AccountShell> }
export function AccountDanger()      { return <AccountShell page="danger"><DangerZone /></AccountShell> }

// ─── AccountShell — shared sidebar nav + heading ──────────────────────

function AccountShell({ children, page }) {
  const scoped = useScopedPath()
  const subnav = [
    { key: 'profile',     label: 'Profile',     to: scoped('account/profile') },
    { key: 'security',    label: 'Security',    to: scoped('account/security') },
    { key: 'preferences', label: 'Preferences', to: scoped('account/preferences') },
    { key: 'danger',      label: 'Account',     to: scoped('account/danger') },
  ]
  return (
    <Shell>
      <Link to={scoped('trips')} style={{ color: '#64748b', fontSize: 13, textDecoration: 'none' }}>← Back to trips</Link>
      <h1 style={{ fontSize: 24, margin: '8px 0 20px' }}>Your account</h1>
      <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <nav style={{ minWidth: 180, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {subnav.map(s => (
            <Link key={s.key} to={s.to} style={{
              padding: '8px 12px', borderRadius: 6,
              background: s.key === page ? '#0f172a' : 'transparent',
              color: s.key === page ? '#fff' : '#334155',
              fontSize: 14, fontWeight: 600,
              textDecoration: 'none',
            }}>{s.label}</Link>
          ))}
        </nav>
        <div style={{ flex: 1, minWidth: 320 }}>
          {children}
        </div>
      </div>
    </Shell>
  )
}

// ─── Profile page ─────────────────────────────────────────────────────

function Profile() {
  const [data, setData] = useState(null)
  const [err, setErr] = useState(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const navigate = useNavigate()
  const scoped = useScopedPath()

  useEffect(() => {
    API.get('/me/profile', { headers: authHeaders() }).then(r => {
      if (r.status === 401) return navigate(scoped('login'))
      if (r.status >= 400) return setErr(r.data?.message)
      setData(r.data.profile)
    })
  }, [])

  async function save(e) {
    e.preventDefault()
    setSaving(true); setErr(null); setSaved(false)
    const r = await API.put('/me/profile', data, { headers: authHeaders() })
    setSaving(false)
    if (r.status >= 400) return setErr(r.data?.message || 'Could not save')
    setSaved(true)
  }
  function set(k, v) { setData(d => ({ ...d, [k]: v })) }

  if (err && !data) return <Box>{err}</Box>
  if (!data) return <Box>Loading...</Box>
  return (
    <form onSubmit={save}>
      <Section title="Your information">
        <FieldRow><Label>Email</Label><div style={{ ...inp, background: '#f1f5f9', color: '#64748b' }}>{data.email}</div></FieldRow>
        <FieldRow><Label>First name *</Label><input value={data.firstName} onChange={e => set('firstName', e.target.value)} style={inp} required /></FieldRow>
        <FieldRow><Label>Last name</Label><input value={data.lastName} onChange={e => set('lastName', e.target.value)} style={inp} /></FieldRow>
        <FieldRow><Label>Phone</Label><input value={data.phone} onChange={e => set('phone', e.target.value)} style={inp} placeholder="+1-902-555-0123" /></FieldRow>
      </Section>
      <Section title="Mailing address">
        <FieldRow><Label>Address line 1</Label><input value={data.addressLine1} onChange={e => set('addressLine1', e.target.value)} style={inp} /></FieldRow>
        <FieldRow><Label>Address line 2</Label><input value={data.addressLine2} onChange={e => set('addressLine2', e.target.value)} style={inp} /></FieldRow>
        <FieldRow><Label>City</Label><input value={data.city} onChange={e => set('city', e.target.value)} style={inp} /></FieldRow>
        <FieldRow><Label>Region / Province / State</Label><input value={data.region} onChange={e => set('region', e.target.value)} style={inp} /></FieldRow>
        <FieldRow><Label>Postal / ZIP code</Label><input value={data.postalCode} onChange={e => set('postalCode', e.target.value)} style={inp} /></FieldRow>
        <FieldRow><Label>Country</Label><input value={data.country} onChange={e => set('country', e.target.value)} style={inp} /></FieldRow>
      </Section>
      {err && <ErrorBox>{err}</ErrorBox>}
      {saved && <SuccessBox>Saved.</SuccessBox>}
      <div style={{ marginTop: 16 }}>
        <button type="submit" disabled={saving} style={primaryBtn}>{saving ? 'Saving...' : 'Save changes'}</button>
      </div>
    </form>
  )
}

// ─── Security page ────────────────────────────────────────────────────

function Security() {
  const [profile, setProfile] = useState(null)
  const navigate = useNavigate()
  const scoped = useScopedPath()
  function reload() {
    return API.get('/me/profile', { headers: authHeaders() }).then(r => {
      if (r.status === 401) return navigate(scoped('login'))
      if (r.status >= 400) return
      setProfile(r.data.profile)
    })
  }
  useEffect(() => { reload() }, [])
  if (!profile) return <Box>Loading...</Box>
  return (
    <>
      <ChangePassword />
      <Mfa profile={profile} onChanged={reload} />
    </>
  )
}

function ChangePassword() {
  const [cur, setCur] = useState('')
  const [next, setNext] = useState('')
  const [conf, setConf] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const [ok, setOk] = useState(false)

  async function submit(e) {
    e.preventDefault()
    setErr(null); setOk(false)
    if (next.length < 8) return setErr('New password must be at least 8 characters')
    if (next !== conf) return setErr('Passwords don\'t match')
    setBusy(true)
    const r = await API.put('/me/password', { currentPassword: cur, newPassword: next }, { headers: authHeaders() })
    setBusy(false)
    if (r.status >= 400) return setErr(r.data?.message || 'Could not change password')
    setOk(true); setCur(''); setNext(''); setConf('')
  }
  return (
    <Section title="Change password">
      <form onSubmit={submit}>
        <FieldRow><Label>Current password</Label><input type="password" value={cur} onChange={e => setCur(e.target.value)} style={inp} autoComplete="current-password" /></FieldRow>
        <FieldRow><Label>New password</Label><input type="password" value={next} onChange={e => setNext(e.target.value)} style={inp} autoComplete="new-password" /></FieldRow>
        <FieldRow><Label>Confirm new password</Label><input type="password" value={conf} onChange={e => setConf(e.target.value)} style={inp} autoComplete="new-password" /></FieldRow>
        {err && <ErrorBox>{err}</ErrorBox>}
        {ok && <SuccessBox>Password changed.</SuccessBox>}
        <div style={{ marginTop: 12 }}>
          <button type="submit" disabled={busy || !next} style={primaryBtn}>{busy ? 'Updating...' : 'Update password'}</button>
        </div>
      </form>
    </Section>
  )
}

function Mfa({ profile, onChanged }) {
  const [step, setStep] = useState('idle') // idle | setup | verify | enabled | disable
  const [setupData, setSetupData] = useState(null)
  const [code, setCode] = useState('')
  const [pwd, setPwd] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const [backupCodes, setBackupCodes] = useState(null)

  async function startSetup() {
    setBusy(true); setErr(null)
    const r = await API.post('/me/mfa/setup', {}, { headers: authHeaders() })
    setBusy(false)
    if (r.status >= 400) return setErr(r.data?.message)
    setSetupData(r.data); setStep('verify')
  }
  async function verifySetup(e) {
    e.preventDefault(); setBusy(true); setErr(null)
    const r = await API.post('/me/mfa/verify-setup', { code }, { headers: authHeaders() })
    setBusy(false)
    if (r.status >= 400) return setErr(r.data?.message)
    setBackupCodes(r.data.backupCodes)
    setStep('enabled')
    onChanged?.()
  }
  async function disable() {
    setBusy(true); setErr(null)
    const r = await API.delete('/me/mfa', { headers: authHeaders(), data: { password: pwd } })
    setBusy(false)
    if (r.status >= 400) return setErr(r.data?.message)
    setStep('idle'); setPwd(''); setBackupCodes(null); setSetupData(null)
    onChanged?.()
  }
  async function regen() {
    setBusy(true); setErr(null)
    const r = await API.post('/me/mfa/regenerate-backup-codes', { password: pwd }, { headers: authHeaders() })
    setBusy(false)
    if (r.status >= 400) return setErr(r.data?.message)
    setBackupCodes(r.data.backupCodes); setPwd('')
  }

  if (profile.mfaEnabled && step !== 'enabled') {
    return (
      <Section title="Two-factor authentication (MFA)">
        <SuccessBox>MFA is enabled (since {new Date(profile.mfaEnabledAt).toLocaleDateString()}).</SuccessBox>
        <p style={{ color: '#64748b', fontSize: 13, lineHeight: 1.6 }}>
          You'll be prompted for a 6-digit code from your authenticator app on each login (or a one-time backup code).
        </p>
        <div style={{ marginTop: 14, padding: 14, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Regenerate backup codes</div>
          <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8 }}>Invalidates your old backup codes and gives you 10 new ones. Save them somewhere safe.</div>
          <input type="password" placeholder="Confirm with your password" value={pwd} onChange={e => setPwd(e.target.value)} style={{ ...inp, marginBottom: 8 }} />
          <button type="button" disabled={busy || !pwd} onClick={regen} style={secondaryBtn}>Regenerate backup codes</button>
        </div>
        {backupCodes && <BackupCodes codes={backupCodes} />}
        <div style={{ marginTop: 14, padding: 14, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8 }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: '#991b1b', marginBottom: 8 }}>Disable MFA</div>
          <div style={{ fontSize: 13, color: '#7f1d1d', marginBottom: 8 }}>Your account will be less secure. Enter your password to confirm.</div>
          <input type="password" placeholder="Password" value={pwd} onChange={e => setPwd(e.target.value)} style={{ ...inp, marginBottom: 8 }} />
          <button type="button" disabled={busy || !pwd} onClick={disable} style={dangerBtn}>Disable MFA</button>
        </div>
        {err && <ErrorBox>{err}</ErrorBox>}
      </Section>
    )
  }

  if (step === 'enabled' && backupCodes) {
    return (
      <Section title="Two-factor authentication (MFA)">
        <SuccessBox>MFA is now active.</SuccessBox>
        <BackupCodes codes={backupCodes} />
      </Section>
    )
  }

  if (step === 'verify' && setupData) {
    return (
      <Section title="Set up two-factor authentication">
        <ol style={{ color: '#475569', fontSize: 14, lineHeight: 1.7, paddingLeft: 20, margin: 0 }}>
          <li>Open your authenticator app (Google Authenticator, 1Password, Authy, etc.)</li>
          <li>Scan this QR code:
            <div style={{ marginTop: 8 }}>
              <img src={setupData.qrDataUrl} alt="MFA QR code" style={{ width: 200, height: 200, background: '#fff', padding: 8, border: '1px solid #e2e8f0', borderRadius: 8 }} />
            </div>
          </li>
          <li>Or paste this secret manually: <code style={{ background: '#f1f5f9', padding: '4px 6px', borderRadius: 4, fontSize: 12 }}>{setupData.secret}</code></li>
          <li>Enter the 6-digit code from the app to confirm:
            <form onSubmit={verifySetup} style={{ marginTop: 8, display: 'flex', gap: 8 }}>
              <input value={code} onChange={e => setCode(e.target.value)} maxLength={6} placeholder="000000" style={{ ...inp, fontFamily: 'monospace', fontSize: 18, letterSpacing: 4, textAlign: 'center', maxWidth: 160 }} />
              <button type="submit" disabled={busy || code.length !== 6} style={primaryBtn}>{busy ? 'Verifying...' : 'Verify and enable'}</button>
            </form>
          </li>
        </ol>
        {err && <ErrorBox>{err}</ErrorBox>}
      </Section>
    )
  }

  return (
    <Section title="Two-factor authentication (MFA)">
      <p style={{ color: '#475569', fontSize: 14, lineHeight: 1.7 }}>
        Add an extra security step to your sign-in. We'll ask for a 6-digit code from your phone after your password.
      </p>
      <button type="button" disabled={busy} onClick={startSetup} style={primaryBtn}>{busy ? 'Loading...' : 'Set up MFA'}</button>
      {err && <ErrorBox>{err}</ErrorBox>}
    </Section>
  )
}

function BackupCodes({ codes }) {
  return (
    <div style={{ marginTop: 12, padding: 14, background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: 8 }}>
      <div style={{ fontWeight: 600, color: '#78350f', marginBottom: 6, fontSize: 13 }}>
        Save these backup codes
      </div>
      <div style={{ fontSize: 12, color: '#92400e', marginBottom: 10 }}>
        Each can be used once if you lose access to your authenticator. Store them somewhere safe — they won't be shown again.
      </div>
      <pre style={{
        background: '#fff', border: '1px solid #fbbf24', borderRadius: 6,
        padding: 10, fontSize: 13, fontFamily: 'monospace', columnCount: 2,
        margin: 0,
      }}>{codes.join('\n')}</pre>
    </div>
  )
}

// ─── Preferences page ─────────────────────────────────────────────────

function Preferences() {
  const [prefs, setPrefs] = useState(null)
  const [saving, setSaving] = useState(false)
  const [ok, setOk] = useState(false)
  const [err, setErr] = useState(null)
  const navigate = useNavigate()
  const scoped = useScopedPath()
  useEffect(() => {
    API.get('/me/email-preferences', { headers: authHeaders() }).then(r => {
      if (r.status === 401) return navigate(scoped('login'))
      if (r.status >= 400) return setErr(r.data?.message)
      setPrefs(r.data.preferences)
    })
  }, [])
  async function save() {
    setSaving(true); setOk(false); setErr(null)
    const r = await API.put('/me/email-preferences', prefs, { headers: authHeaders() })
    setSaving(false)
    if (r.status >= 400) return setErr(r.data?.message)
    setOk(true)
  }
  if (!prefs) return <Box>Loading...</Box>
  const Row = ({ k, label, desc }) => (
    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 0', borderBottom: '1px solid #f1f5f9' }}>
      <input type="checkbox" checked={prefs[k]} onChange={e => setPrefs(p => ({ ...p, [k]: e.target.checked }))} style={{ marginTop: 4 }} />
      <div>
        <div style={{ fontWeight: 600, fontSize: 14 }}>{label}</div>
        <div style={{ color: '#64748b', fontSize: 13, marginTop: 2 }}>{desc}</div>
      </div>
    </label>
  )
  return (
    <Section title="Email preferences">
      <p style={{ color: '#64748b', fontSize: 13, lineHeight: 1.6, margin: '0 0 8px' }}>
        Transactional emails (trip confirmations, action items, password resets) are always sent for safety. You can opt out of everything else below.
      </p>
      <Row k="reminders" label="Trip reminders" desc="7-day and 24-hour reminders before your trip." />
      <Row k="reviews" label="Review requests" desc="One email 5-7 days after your trip asking how it went." />
      <Row k="marketing" label="Marketing" desc="Promotions, package launches, seasonal offers." />
      {err && <ErrorBox>{err}</ErrorBox>}
      {ok && <SuccessBox>Saved.</SuccessBox>}
      <div style={{ marginTop: 16 }}>
        <button type="button" disabled={saving} onClick={save} style={primaryBtn}>{saving ? 'Saving...' : 'Save preferences'}</button>
      </div>
    </Section>
  )
}

// ─── Danger zone page ─────────────────────────────────────────────────

function DangerZone() {
  const [pwd, setPwd] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const navigate = useNavigate()
  const scoped = useScopedPath()

  async function exportData() {
    const t = localStorage.getItem('rcp_session')
    const url = '/api/public/customer-portal/me/account/export'
    // Use fetch + blob so the browser triggers a download with the
    // server-set Content-Disposition.
    const r = await fetch(url, { headers: { Authorization: 'Bearer ' + t } })
    if (!r.ok) return setErr('Could not export')
    const blob = await r.blob()
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = 'account-export.json'
    a.click()
    URL.revokeObjectURL(a.href)
  }

  async function deleteAccount() {
    setBusy(true); setErr(null)
    const r = await API.delete('/me/account', { headers: authHeaders(), data: { password: pwd, confirm } })
    setBusy(false)
    if (r.status >= 400) return setErr(r.data?.message)
    // Sign out + bounce to login
    localStorage.removeItem('rcp_session')
    localStorage.removeItem('rcp_device')
    navigate(scoped('login'))
  }

  return (
    <>
      <Section title="Export your data">
        <p style={{ color: '#475569', fontSize: 14, lineHeight: 1.6 }}>
          Download a JSON file with your profile, every trip, payment history, and messages.
        </p>
        <button type="button" onClick={exportData} style={secondaryBtn}>Download account export</button>
        {err && <ErrorBox>{err}</ErrorBox>}
      </Section>
      <Section title="Delete account">
        <div style={{ padding: 14, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8 }}>
          <div style={{ fontWeight: 600, color: '#991b1b', marginBottom: 6 }}>Permanent and irreversible.</div>
          <div style={{ fontSize: 13, color: '#7f1d1d', marginBottom: 12, lineHeight: 1.6 }}>
            Your personal information (name, email, phone, address) is anonymized immediately.
            Trip records are kept (without your PII) for the reseller's reporting + accounting.
            You'll be signed out and won't be able to log back in.
          </div>
          <FieldRow><Label>Confirm with your password</Label><input type="password" value={pwd} onChange={e => setPwd(e.target.value)} style={inp} /></FieldRow>
          <FieldRow><Label>Type <code>DELETE</code> to confirm</Label><input value={confirm} onChange={e => setConfirm(e.target.value)} style={inp} placeholder="DELETE" /></FieldRow>
          {err && <ErrorBox>{err}</ErrorBox>}
          <button type="button" disabled={busy || confirm !== 'DELETE' || !pwd} onClick={deleteAccount} style={dangerBtn}>
            {busy ? 'Deleting...' : 'Permanently delete my account'}
          </button>
        </div>
      </Section>
    </>
  )
}

// ─── Bits ─────────────────────────────────────────────────────────────

function Section({ title, children }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 20, marginBottom: 16 }}>
      <h3 style={{ margin: '0 0 14px', fontSize: 15, color: '#0f172a' }}>{title}</h3>
      {children}
    </div>
  )
}
function FieldRow({ children }) { return <div style={{ marginBottom: 12 }}>{children}</div> }
function Label({ children }) { return <div style={{ fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 4 }}>{children}</div> }
function Box({ children }) { return <div style={{ color: '#475569', fontSize: 14 }}>{children}</div> }
function ErrorBox({ children }) { return <div style={{ padding: 10, background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 6, fontSize: 13, marginTop: 10 }}>{children}</div> }
function SuccessBox({ children }) { return <div style={{ padding: 10, background: '#d1fae5', color: '#065f46', border: '1px solid #6ee7b7', borderRadius: 6, fontSize: 13, marginTop: 10 }}>{children}</div> }

const inp = { width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' }
const primaryBtn = { padding: '10px 16px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' }
const secondaryBtn = { padding: '10px 14px', background: '#fff', color: '#334155', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }
const dangerBtn = { padding: '10px 16px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' }
